/* ==========================================================================
   KaryaGold — kg-search custom element
   Desktop: input expands left from icon
   Mobile:  icon opens panel that contains its own search bar
   ========================================================================== */

(function () {
  'use strict';

  const MOBILE_BP = window.matchMedia('(max-width: 989px)');

  class KgSearch extends HTMLElement {
    constructor() {
      super();
      this.isOpen = false;
      this.queryCache = {};
      this.debounceTimer = null;
      this.abortController = null;
    }

    connectedCallback() {
      /* ---- Desktop elements (inside <kg-search>) ---- */
      this.toggleBtn = this.querySelector('.kg-search__toggle');
      this.fieldWrap = this.querySelector('.kg-search__field-wrap');
      this.field     = this.querySelector('.kg-search__field');
      this.clearBtn  = this.querySelector('.kg-search__clear');

      /* ---- Panel & overlay (sibling elements in the section) ---- */
      this.panel   = document.querySelector('[data-kg-search-panel]');
      this.overlay = document.querySelector('[data-kg-search-overlay]');

      if (!this.panel || !this.overlay) return;

      /* ---- Panel sub-elements ---- */
      this.colProducts     = this.panel.querySelector('[data-kg-search-products]');
      this.initialProducts = this.panel.querySelector('[data-kg-initial-products]');
      this.resultProducts  = this.panel.querySelector('[data-kg-result-products]');
      this.colSuggestions  = this.panel.querySelector('[data-kg-search-suggestions]');

      /* ---- Mobile search bar (inside the panel) ---- */
      this.mobileField    = this.panel.querySelector('[data-kg-mobile-field]');
      this.mobileClearBtn = this.panel.querySelector('[data-kg-mobile-clear]');

      /* ---- Events ---- */
      this.toggleBtn.addEventListener('click', () =>
        this.isOpen ? this.close() : this.open()
      );

      this.overlay.addEventListener('click', () => this.close());

      // Desktop input
      if (this.field) {
        this.field.addEventListener('input', () => this._onDesktopInput());
        this.field.addEventListener('keydown', (e) => this._onKeydown(e));
        this.field.closest('form')?.addEventListener('submit', (e) => {
          if (!this.field.value.trim()) e.preventDefault();
        });
      }

      // Desktop clear button
      this.clearBtn?.addEventListener('click', () => {
        this.field.value = '';
        this.clearBtn.setAttribute('hidden', '');
        this._clearResults();
        this.field.focus();
      });

      // Mobile input
      if (this.mobileField) {
        this.mobileField.addEventListener('input', () => this._onMobileInput());
        this.mobileField.addEventListener('keydown', (e) => this._onKeydown(e));
        this.mobileField.closest('form')?.addEventListener('submit', (e) => {
          if (!this.mobileField.value.trim()) e.preventDefault();
        });
      }

      // Mobile clear button
      this.mobileClearBtn?.addEventListener('click', () => {
        this.mobileField.value = '';
        this.mobileClearBtn.setAttribute('hidden', '');
        this._clearResults();
        this.mobileField.focus();
      });

      // Escape closes
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
          this.toggleBtn.focus();
        }
      });

      // Click outside both kg-search and panel closes
      document.addEventListener('pointerdown', (e) => {
        if (
          this.isOpen &&
          !this.contains(e.target) &&
          !this.panel.contains(e.target)
        ) {
          this.close();
        }
      });
    }

    /* ------------------------------------------------------------------
       Open / close
       ------------------------------------------------------------------ */

    open() {
      this.isOpen = true;
      this._setPanelTop();
      document.body.classList.add('kg-search-open');
      this.toggleBtn.setAttribute('aria-expanded', 'true');
      this.panel.removeAttribute('aria-hidden');
      this.overlay.removeAttribute('aria-hidden');

      if (this.fieldWrap) {
        this.fieldWrap.removeAttribute('aria-hidden');
        this.field?.removeAttribute('tabindex');
      }

      // Focus the correct input after transition starts
      requestAnimationFrame(() => {
        if (MOBILE_BP.matches) {
          // Mobile: focus the panel search bar
          this.mobileField?.removeAttribute('tabindex');
          this.mobileField?.focus();
        } else {
          // Desktop: focus the expanding field
          this.field?.focus();
        }
      });
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      document.body.classList.remove('kg-search-open');
      this.toggleBtn.setAttribute('aria-expanded', 'false');
      this.panel.setAttribute('aria-hidden', 'true');
      this.overlay.setAttribute('aria-hidden', 'true');

      // Reset desktop input
      if (this.field) {
        this.field.value = '';
        this.field.setAttribute('tabindex', '-1');
      }
      if (this.fieldWrap) this.fieldWrap.setAttribute('aria-hidden', 'true');
      this.clearBtn?.setAttribute('hidden', '');

      // Reset mobile input
      if (this.mobileField) {
        this.mobileField.value = '';
        this.mobileField.setAttribute('tabindex', '-1');
      }
      this.mobileClearBtn?.setAttribute('hidden', '');

      clearTimeout(this.debounceTimer);
      this.abortController?.abort();
      this._clearResults();
    }

    /* ------------------------------------------------------------------
       Panel positioning — sit just under the header at open time.

       Hero pages (body.kg-has-hero):
         The section-header is position:fixed; top:0 and is visually
         translated down by the announcement bar height while at the top
         of the page. We use offsetHeight (layout box, ignores transform)
         so the panel sits at the header's logical bottom — when the
         header is transparent at the top this is the visually correct
         attach point and avoids a large gap below the announcement bar.

       Other templates:
         The section-header sits in document flow below the announcement
         bar. getBoundingClientRect().bottom returns the visual bottom in
         the viewport, which lands the panel exactly under the rendered
         header regardless of sticky/scroll state.
       ------------------------------------------------------------------ */

    _setPanelTop() {
      const headerEl = document.querySelector('.section-header');
      if (!headerEl) return;

      const isHero = document.body.classList.contains('kg-has-hero');
      const top = isHero
        ? headerEl.offsetHeight
        : Math.max(0, headerEl.getBoundingClientRect().bottom);

      document.documentElement.style.setProperty(
        '--kg-search-panel-top',
        `${top}px`
      );
    }

    /* ------------------------------------------------------------------
       Input handling
       ------------------------------------------------------------------ */

    _onDesktopInput() {
      // Toggle clear button (only when NOT hidden by [hidden] attr conflict)
      if (this.clearBtn) {
        const hasText = this.field.value.length > 0;
        if (hasText) {
          this.clearBtn.removeAttribute('hidden');
        } else {
          this.clearBtn.setAttribute('hidden', '');
        }
      }
      this._scheduleSearch(this.field.value.trim());
    }

    _onMobileInput() {
      if (this.mobileClearBtn) {
        const hasText = this.mobileField.value.length > 0;
        if (hasText) {
          this.mobileClearBtn.removeAttribute('hidden');
        } else {
          this.mobileClearBtn.setAttribute('hidden', '');
        }
      }
      this._scheduleSearch(this.mobileField.value.trim());
    }

    _onKeydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
        this.toggleBtn.focus();
      }
    }

    _scheduleSearch(query) {
      clearTimeout(this.debounceTimer);
      if (!query || query.length < 2) {
        this._clearResults();
        return;
      }
      this.debounceTimer = setTimeout(() => this._search(query), 320);
    }

    /* ------------------------------------------------------------------
       Fetch — product cards + query suggestions in parallel
       ------------------------------------------------------------------ */

    async _search(query) {
      const key = query.toLowerCase();

      if (this.queryCache[key]) {
        this._renderResults(this.queryCache[key]);
        return;
      }

      this.abortController?.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.colProducts?.setAttribute('data-loading', '');

      try {
        const [prodRes, sugRes] = await Promise.all([
          fetch(
            `/search?q=${encodeURIComponent(query)}&type=product&section_id=kg-search-products`,
            { signal }
          ),
          fetch(
            `${this.dataset.predictiveUrl}?q=${encodeURIComponent(query)}` +
              `&section_id=predictive-search&resources[type]=query&resources[limit]=6`,
            { signal }
          ),
        ]);

        const [prodText, sugText] = await Promise.all([
          prodRes.text(),
          sugRes.text(),
        ]);

        const result = { prodText, sugText, query };
        this.queryCache[key] = result;
        this._renderResults(result);
      } catch (err) {
        if (err.name !== 'AbortError') console.warn('[kg-search]', err);
      } finally {
        this.colProducts?.removeAttribute('data-loading');
      }
    }

    /* ------------------------------------------------------------------
       Render
       ------------------------------------------------------------------ */

    _renderResults({ prodText, sugText, query }) {
      this._renderProducts(prodText, query);
      this._renderSuggestions(sugText, query);
    }

    _renderProducts(html, query) {
      if (!this.resultProducts) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const section = doc.querySelector('#shopify-section-kg-search-products');
      if (!section) return;

      let markup = section.innerHTML;

      // Append "see all results" link here so it's visible on mobile too
      if (query) {
        const searchHref = `${this.dataset.searchUrl}?q=${encodeURIComponent(query)}&type=product`;
        markup += `
          <a href="${searchHref}" class="kg-search-panel__search-for-link">
            <span>"${query}" için tüm sonuçları gör</span>
            <span class="kg-search-panel__search-for-arrow">→</span>
          </a>
        `;
      }

      this.resultProducts.innerHTML = markup;
      this.resultProducts.classList.add('is-visible');
      if (this.initialProducts) this.initialProducts.hidden = true;
    }

    _renderSuggestions(html, query) {
      if (!this.colSuggestions) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const section = doc.querySelector('#shopify-section-predictive-search');
      if (!section) return;

      const queriesList = section.querySelector(
        '#predictive-search-results-queries-list'
      );

      let markup = '';

      if (queriesList && queriesList.children.length > 0) {
        markup += `<p class="kg-search-panel__suggestions-label">Öneriler</p>`;
        markup += queriesList.outerHTML;
      }

      this.colSuggestions.innerHTML = markup;

      this.colSuggestions
        .querySelectorAll('.predictive-search__item')
        .forEach((el) => el.removeAttribute('tabindex'));
    }

    /* ------------------------------------------------------------------
       Clear — restore initial / featured products
       ------------------------------------------------------------------ */

    _clearResults() {
      if (this.resultProducts) {
        this.resultProducts.innerHTML = '';
        this.resultProducts.classList.remove('is-visible');
      }
      if (this.colSuggestions) this.colSuggestions.innerHTML = '';
      if (this.initialProducts) this.initialProducts.hidden = false;
    }
  }

  if (!customElements.get('kg-search')) {
    customElements.define('kg-search', KgSearch);
  }
})();
